import { useEffect, useRef } from "react";

import { Typography, useStepperDispatch } from "@king-ict/eupisi-ui/components";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { questionIdsToHack, questionsToMakeRequired } from "./constants";
import { useAppDispatch } from "../../../app/store";
import QuestionDisplayTypeEnum from "../../../models/Questionnaire/QuestionDisplayTypeEnum";
import InitialConversationQuestionDataItemValidationSchema from "../../../models/Request/InitialConversationQuestionDataItemValidationSchema";
import { updateInsertQuestionAnswer } from "../../../slices/requestSlice";
import { RequestFormCard, RequestFormTopMessage } from "../RequestComponents";
import QuestionComponentFactory from "./QuestionComponentFactory";
import { useInitialConversationStep } from "./useInitialConversationStep";

interface Props {
  subStep: number;
}

export default function InitialConversationStep({ subStep }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [
    questionGroup,
    questionGroupConversationItems,
    setQuestionGroupConversationItems,
  ] = useInitialConversationStep({
    subStep: subStep,
  });

  // Track the previous state of questions to know it's parent's answer
  const previousAnswersRef = useRef<Record<number, number | undefined>>({});

  const [errors, doValidations, stopValidations] = useValidation({
    data: questionGroupConversationItems,
    validationSchema: InitialConversationQuestionDataItemValidationSchema,
  });

  // Effect to handle child question reset when parent answer changes
  useEffect(() => {
    if (!questionGroupConversationItems.length) return;

    // Extract current answers for tracked questions
    const currentAnswers: Record<number, number | undefined> = {};
    questionIdsToHack.forEach(({ questionGroupQuestionId }) => {
      const item = questionGroupConversationItems.find(
        (item) => item.questionGroupQuestionId === questionGroupQuestionId
      );
      if (item) {
        currentAnswers[questionGroupQuestionId] = item.answerId;
      }
    });

    // Find which specific question has changed
    const changedQuestionIds: number[] = [];
    questionIdsToHack.forEach(({ questionGroupQuestionId }) => {
      if (
        previousAnswersRef.current[questionGroupQuestionId] !== undefined &&
        previousAnswersRef.current[questionGroupQuestionId] !==
          currentAnswers[questionGroupQuestionId]
      ) {
        changedQuestionIds.push(questionGroupQuestionId);
      }
    });

    // Update previous answers reference
    previousAnswersRef.current = currentAnswers;

    // Skip if no relevant changes
    if (changedQuestionIds.length === 0) {
      return;
    }

    // Check if any question with childQuestionId was changed from yes to no/other
    for (const questionHack of questionIdsToHack) {
      if (questionHack.childQuestionId && questionHack.resetChildToAnswerId) {
        const questionId = questionHack.questionGroupQuestionId;

        // Skip if this question didn't change
        if (!changedQuestionIds.includes(questionId)) {
          continue;
        }

        const wasAnswerYes =
          previousAnswersRef.current[questionId] ===
          questionHack.answerIdToShow;
        const isAnswerNotYes =
          currentAnswers[questionId] !== questionHack.answerIdToShow;

        // If the answer changed from "Yes" to something else
        if (wasAnswerYes && isAnswerNotYes) {
          // Find the child question in the list
          const childQuestion = questionGroupConversationItems.find(
            (item) =>
              item.questionGroupQuestionId === questionHack.childQuestionId
          );

          const childQuestionHack = questionIdsToHack.find(
            (hack) =>
              hack.questionGroupQuestionId === questionHack.childQuestionId
          );

          // If the child question exists and has the "show" answer selected
          if (
            childQuestion &&
            childQuestionHack &&
            childQuestion.answerId === childQuestionHack.answerIdToShow
          ) {
            // Reset the child question's answer
            setQuestionGroupConversationItems(
              questionGroupConversationItems.map((item) =>
                item.questionGroupQuestionId === questionHack.childQuestionId
                  ? { ...item, answerId: questionHack.resetChildToAnswerId }
                  : item
              )
            );

            // Remove the child question's answer from the Redux store too
            if (questionHack.childQuestionId) {
              dispatch(
                updateInsertQuestionAnswer({
                  questionId: questionHack.childQuestionId,
                  answerId: questionHack.resetChildToAnswerId,
                  answerIds: [],
                  answerText: undefined,
                })
              );
            }
          }
        }
      }
    }
  }, [questionGroupConversationItems, dispatch]);

  const handleStep = () => {
    if (!questionGroupConversationItems) return false;

    const isValid = doValidations();

    if (isValid) {
      stopValidations();
    }

    return isValid;
  };

  useStepperDispatch({
    type: "next",
    payload: handleStep,
  });

  useStepperDispatch({
    type: "previous",
    payload: handleStep,
  });

  useStepperDispatch({
    type: "save",
    payload: () => {},
  });

  const renderQuestions = () => {
    const repeatableSetLength = questionGroupConversationItems.filter(
      (item) => item.sort === 0
    ).length;

    const questionItems = questionGroupConversationItems.map((cd, i) => {
      const question = questionGroup?.questions.find(
        (q) => q.questionGroupQuestionId === cd.questionGroupQuestionId
      );

      if (questionsToMakeRequired.includes(cd.questionGroupQuestionId)) {
        cd.isRequired = true;
      }

      if (!question) {
        return null;
      }

      const isLastQuestionInRepeatableSet = (i + 1) % repeatableSetLength === 0;

      let errorText;
      if (errors !== undefined) {
        switch (question.displayTypeId) {
          case QuestionDisplayTypeEnum.Text:
          case QuestionDisplayTypeEnum.TextArea:
          case QuestionDisplayTypeEnum.Time:
          case QuestionDisplayTypeEnum.Date:
            errorText = errors[i]?.answer;
            break;
          case QuestionDisplayTypeEnum.Select:
            errorText = errors[i]?.answerId;
            break;
          case QuestionDisplayTypeEnum.Radio:
            errorText = errors[i]?.answerId
              ? t("validations.radioGroupRequired")
              : undefined;
            break;
          case QuestionDisplayTypeEnum.MultiSelect:
            errorText = errors[i]?.answerIds;
            break;
        }
      }

      return (
        <>
          <QuestionComponentFactory
            key={
              cd.isRepeatable
                ? `${cd.questionGroupQuestionId}_${cd.sort}`
                : cd.questionGroupQuestionId
            }
            stateIndex={i}
            question={question!}
            conversationItem={cd}
            setState={setQuestionGroupConversationItems}
            errorText={errorText}
            allQuestionItems={questionGroupConversationItems}
          />
          {isLastQuestionInRepeatableSet && (
            <Row>
              <Col xs={12}>
                <hr />
              </Col>
            </Row>
          )}
        </>
      );
    });

    return questionItems;
  };

  return (
    <>
      <RequestFormTopMessage
        title={t("initialConversationStep.mainInfoTitle")}
        infoMessage={t("initialConversationStep.mainInfoMessage")}
      />
      <Typography variant="medium" color="light">
        <i>{t("initialConversationStep.gdpr1")}</i>
      </Typography>
      <Typography variant="medium" color="light">
        <i>{t("initialConversationStep.gdpr2")}</i>
      </Typography>
      <RequestFormCard cardTitle={questionGroup?.name}>
        <Row>
          <Col />
          <Col xs={12} lg={6}>
            {renderQuestions()}
          </Col>
          <Col />
        </Row>
      </RequestFormCard>
    </>
  );
}
